import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { VideoView, useVideoPlayer } from 'expo-video';
import { router } from 'expo-router';
import { useIsFocused } from "expo-router/react-navigation";
import { makeAutoObservable, reaction } from 'mobx';
import { observer } from 'mobx-react';

import DefaultBackground from '@/components/DefaultBackground';
import TopbarLogo from '@/components/TopbarLogo';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { useScale } from '@/hooks/useScale';
import { colors } from '@/constants/colors';
import {
  DEFAULT_VIDEO_DEBUG_SOURCE_ID,
  getVariationById,
  getVideoSourceById,
  type VideoDebugSource,
  type VideoDebugVariation,
} from '@/config/videoDebugVariations';
import { deleteCachedVideo, downloadVideo } from '@/services/videoDownload';
import type {
  DownloadHandle,
  DownloadProgress,
} from '@/services/videoDownload.types';

type Props = {
  variationId: string | undefined;
  videoId?: string | undefined;
};

type DownloadState =
  | { kind: 'idle' }
  | { kind: 'downloading'; progress: number }
  | { kind: 'ready'; uri: string; streamed: boolean }
  | { kind: 'error'; message: string };

const MAX_LOG_LINES = 40;

class LogStore {
  lines: string[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  append(line: string) {
    if (this.lines.length >= MAX_LOG_LINES) {
      this.lines.shift();
    }
    this.lines.push(line);
  }
}

class PlayerInfoStore {
  status: string = 'unknown';
  error: string | null = null;
  downloadState: DownloadState;

  constructor(initial: DownloadState) {
    this.downloadState = initial;
    makeAutoObservable(this);
  }

  setStatus(status: string, error: string | null) {
    this.status = status;
    this.error = error;
  }

  setDownloadState(state: DownloadState) {
    this.downloadState = state;
  }

  get playbackUri(): string | null {
    return this.downloadState.kind === 'ready' ? this.downloadState.uri : null;
  }
}

export default function VideoDebugVariationScreen({
  variationId,
  videoId,
}: Props) {
  const styles = useStyles();
  const isFocused = useIsFocused();

  const variation = getVariationById(variationId);
  const source = getVideoSourceById(videoId ?? DEFAULT_VIDEO_DEBUG_SOURCE_ID);

  if (!variation || !source) {
    return (
      <ErrorState
        message={
          !variation
            ? `Unknown variation: "${variationId}"`
            : `Unknown video id: "${videoId}"`
        }
      />
    );
  }

  if (!isFocused) {
    return null;
  }

  return (
    <ResolvedScreen
      key={`${variation.id}:${source.id}`}
      variation={variation}
      source={source}
      styles={styles}
    />
  );
}

function ResolvedScreen({
  variation,
  source,
  styles,
}: {
  variation: VideoDebugVariation;
  source: VideoDebugSource;
  styles: ReturnType<typeof useStyles>;
}) {
  const logTag = `[VideoDebug:${variation.id}]`;
  const logStoreRef = useRef<LogStore | null>(null);
  if (logStoreRef.current === null) logStoreRef.current = new LogStore();
  const logStore = logStoreRef.current;

  const appendLog = useCallback(
    (message: string) => {
      const line = `${new Date().toISOString().slice(11, 19)} ${message}`;
      console.log(`${logTag} ${message}`);
      logStore.append(line);
    },
    [logTag, logStore],
  );

  console.log('Render resolved screen');

  const infoStoreRef = useRef<PlayerInfoStore | null>(null);
  if (infoStoreRef.current === null) {
    infoStoreRef.current = new PlayerInfoStore(
      variation.sourceMode === 'stream'
        ? { kind: 'ready', uri: source.url, streamed: true }
        : { kind: 'idle' },
    );
  }
  const infoStore = infoStoreRef.current;

  useEffect(() => {
    appendLog(
      `mounted — surfaceType=${variation.surfaceType} sourceMode=${variation.sourceMode} url=${source.url}`,
    );
    return () => {
      console.log(`${logTag} unmounted`);
    };
  }, [appendLog, logTag, variation, source]);

  const downloadHandleRef = useRef<DownloadHandle | null>(null);
  const [downloadNonce, setDownloadNonce] = useState(0);

  useEffect(() => {
    if (variation.sourceMode !== 'download') return;

    let active = true;
    infoStore.setDownloadState({ kind: 'downloading', progress: 0 });
    appendLog('download started');

    const onProgress = (p: DownloadProgress) => {
      if (!active) return;
      infoStore.setDownloadState({ kind: 'downloading', progress: p.progress });
    };

    const handle = downloadVideo(source.url, onProgress);
    downloadHandleRef.current = handle;

    handle.promise
      .then((result) => {
        if (!active) return;
        appendLog(
          `download complete — uri=${result.uri} streamed=${result.streamed}`,
        );
        infoStore.setDownloadState({
          kind: 'ready',
          uri: result.uri,
          streamed: result.streamed,
        });
      })
      .catch((err: unknown) => {
        if (!active) return;
        const message = err instanceof Error ? err.message : String(err);
        appendLog(`download failed — ${message}`);
        infoStore.setDownloadState({ kind: 'error', message });
      });

    return () => {
      active = false;
      if (downloadHandleRef.current) {
        downloadHandleRef.current.cancel();
        downloadHandleRef.current = null;
      }
    };
  }, [variation.sourceMode, source.url, downloadNonce, appendLog, infoStore]);

  const player = useVideoPlayer(null, (p) => {
    p.loop = false;
    p.muted = false;
    p.volume = 1.0;
  });

  useEffect(() => {
    const dispose = reaction(
      () => infoStore.playbackUri,
      (uri) => {
        if (!uri) return;
        appendLog(`replaceAsync — uri=${uri}`);
        player.replaceAsync(uri).catch((err: unknown) => {
          appendLog(`replaceAsync failed — ${String(err)}`);
        });
      },
      { fireImmediately: true },
    );
    return dispose;
  }, [player, infoStore, appendLog]);

  useEffect(() => {
    const statusSub = player.addListener('statusChange', (payload) => {
      appendLog(
        `statusChange status=${payload.status} oldStatus=${payload.oldStatus ?? 'n/a'}${
          payload.error ? ` error=${JSON.stringify(payload.error)}` : ''
        }`,
      );
      infoStore.setStatus(
        payload.status,
        payload.error
          ? (payload.error.message ?? JSON.stringify(payload.error))
          : null,
      );
      if (payload.status === 'readyToPlay') {
        try {
          player.play();
          appendLog('auto-play triggered');
        } catch (err) {
          appendLog(`play() threw — ${String(err)}`);
        }
      }
    });

    const playingSub = player.addListener('playingChange', (payload) => {
      appendLog(`playingChange isPlaying=${payload.isPlaying}`);
    });

    const endSub = player.addListener('playToEnd', () => {
      appendLog('playToEnd');
    });

    const sourceLoadSub = player.addListener('sourceLoad', () => {
      appendLog(
        `sourceLoad — duration=${player.duration} status=${player.status}`,
      );
    });

    return () => {
      statusSub.remove();
      playingSub.remove();
      endSub.remove();
      sourceLoadSub.remove();
    };
  }, [player, appendLog, infoStore]);

  const forceRedownload = useCallback(async () => {
    appendLog('force re-download requested');
    try {
      await deleteCachedVideo(source.url);
      appendLog('cached file deleted');
    } catch (err) {
      appendLog(`deleteCachedVideo failed — ${String(err)}`);
    }
    infoStore.setDownloadState({ kind: 'idle' });
    setDownloadNonce((n) => n + 1);
  }, [appendLog, source.url, infoStore]);

  return (
    <ThemedView style={styles.container}>
      {variation.gradientBackground && <DefaultBackground />}

      <View style={styles.topBar}>
        <Pressable
          style={({ focused }) => [
            styles.backButton,
            focused && styles.backButtonFocused,
          ]}
          onPress={() => router.back()}
        >
          <ThemedText style={styles.backButtonText}>Back</ThemedText>
        </Pressable>
        <View style={styles.topBarSpacer} />
        <TopbarLogo />
        <View style={styles.topBarSpacer} />
        <View style={styles.backButtonPlaceholder} />
      </View>

      <ThemedText style={styles.title}>{variation.label}</ThemedText>

      <View style={styles.mainRow}>
        <View
          style={[
            styles.videoWrapper,
            variation.wrapperHeightMode === 'full' &&
              styles.videoWrapperFullHeight,
            variation.wrapperRounded && styles.videoWrapperRounded,
            variation.wrapperBackground === 'semitransparent' &&
              styles.videoWrapperSemitransparent,
          ]}
        >
          <VideoView
            player={player}
            surfaceType={variation.surfaceType}
            style={styles.video}
            nativeControls={false}
            contentFit="contain"
          />
        </View>

        <View style={styles.sideColumn}>
          <StatusCard
            infoStore={infoStore}
            variation={variation}
            source={source}
            styles={styles}
          />

          {variation.sourceMode === 'download' && (
            <Pressable
              onPress={forceRedownload}
              style={({ focused }) => [
                styles.button,
                focused && styles.buttonFocused,
              ]}
            >
              <ThemedText style={styles.buttonText}>
                Force re-download
              </ThemedText>
            </Pressable>
          )}

          <LogPanel store={logStore} styles={styles} />
        </View>
      </View>
    </ThemedView>
  );
}

const StatusCard = observer(function StatusCard({
  infoStore,
  variation,
  source,
  styles,
}: {
  infoStore: PlayerInfoStore;
  variation: VideoDebugVariation;
  source: VideoDebugSource;
  styles: ReturnType<typeof useStyles>;
}) {
  return (
    <View style={styles.statusCard}>
      <StatusRow label="variation" value={variation.id} />
      <StatusRow label="surface" value={variation.surfaceType} />
      <StatusRow label="source" value={variation.sourceMode} />
      <StatusRow label="wrapper" value={describeWrapperFlags(variation)} />
      <StatusRow
        label="screen bg"
        value={variation.gradientBackground ? 'gradient' : 'solid'}
      />
      <StatusRow label="url" value={source.url} truncate />
      <StatusRow label="platform" value={Platform.OS} />
      <StatusRow label="player.status" value={infoStore.status} />
      {infoStore.error && (
        <StatusRow label="player.error" value={infoStore.error} />
      )}
      {variation.sourceMode === 'download' && (
        <StatusRow
          label="download"
          value={describeDownloadState(infoStore.downloadState)}
        />
      )}
    </View>
  );
});

const LogPanel = observer(function LogPanel({
  store,
  styles,
}: {
  store: LogStore;
  styles: ReturnType<typeof useStyles>;
}) {
  const lines = store.lines;
  const reversed = lines.slice().reverse();

  return (
    <View style={styles.logPanel}>
      <ThemedText style={styles.logTitle}>Logs (newest first)</ThemedText>
      <ScrollView
        style={styles.logScroll}
        contentContainerStyle={styles.logContent}
      >
        {reversed.length === 0 ? (
          <ThemedText style={styles.logLineMuted}>
            Waiting for events…
          </ThemedText>
        ) : (
          reversed.map((line, idx) => (
            <ThemedText key={lines.length - 1 - idx} style={styles.logLine}>
              {line}
            </ThemedText>
          ))
        )}
      </ScrollView>
    </View>
  );
});

function StatusRow({
  label,
  value,
  truncate,
}: {
  label: string;
  value: string;
  truncate?: boolean;
}) {
  const styles = useStyles();
  return (
    <View style={styles.statusRow}>
      <ThemedText style={styles.statusLabel}>{label}</ThemedText>
      <ThemedText
        style={styles.statusValue}
        numberOfLines={truncate ? 1 : undefined}
        ellipsizeMode="middle"
      >
        {value}
      </ThemedText>
    </View>
  );
}

function ErrorState({ message }: { message: string }) {
  const styles = useStyles();
  return (
    <ThemedView style={styles.container}>
      <View style={styles.topBar}>
        <Pressable
          style={({ focused }) => [
            styles.backButton,
            focused && styles.backButtonFocused,
          ]}
          onPress={() => router.back()}
        >
          <ThemedText style={styles.backButtonText}>Back</ThemedText>
        </Pressable>
        <View style={styles.topBarSpacer} />
        <TopbarLogo />
        <View style={styles.topBarSpacer} />
        <View style={styles.backButtonPlaceholder} />
      </View>
      <ThemedText style={styles.title}>Video Debug</ThemedText>
      <View style={styles.statusCard}>
        <ThemedText style={styles.statusValue}>{message}</ThemedText>
      </View>
    </ThemedView>
  );
}

function describeWrapperFlags(variation: VideoDebugVariation): string {
  const parts = [
    variation.wrapperHeightMode === 'full' ? 'full-height' : 'fixed-height',
    variation.wrapperRounded ? 'rounded' : null,
    variation.wrapperBackground === 'semitransparent'
      ? 'semitransparent-bg'
      : 'opaque-bg',
  ];
  return parts.filter((p): p is string => p !== null).join(' · ');
}

function describeDownloadState(state: DownloadState): string {
  switch (state.kind) {
    case 'idle':
      return 'idle';
    case 'downloading':
      return `downloading ${state.progress}%`;
    case 'ready':
      return state.streamed ? 'streaming (cache fallback)' : 'downloaded';
    case 'error':
      return `error — ${state.message}`;
  }
}

function useStyles() {
  const scale = useScale();
  return useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: colors.brand.primary,
        },
        topBar: {
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          paddingVertical: 24 * scale,
          paddingHorizontal: 24 * scale,
        },
        topBarSpacer: {
          flex: 1,
        },
        backButton: {
          paddingVertical: 8 * scale,
          paddingHorizontal: 20 * scale,
          backgroundColor: 'rgba(255,255,255,0.15)',
          borderRadius: 20 * scale,
          borderWidth: 2,
          borderColor: 'transparent',
        },
        backButtonFocused: {
          backgroundColor: 'rgba(255,255,255,0.3)',
          borderColor: '#fff',
        },
        backButtonPlaceholder: {
          width: 80 * scale,
        },
        backButtonText: {
          color: '#fff',
          fontSize: 14 * scale,
          fontWeight: '600',
        },
        title: {
          color: '#fff',
          fontSize: 24 * scale,
          fontWeight: 'bold',
          textAlign: 'center',
          marginBottom: 12 * scale,
        },
        mainRow: {
          flex: 1,
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 20 * scale,
          marginHorizontal: 40 * scale,
          marginBottom: 24 * scale,
        },
        videoWrapper: {
          aspectRatio: 9 / 16,
          height: 320 * scale,
          backgroundColor: '#000000',
        },
        videoWrapperFullHeight: {
          height: '100%',
        },
        videoWrapperRounded: {
          borderRadius: 20 * scale,
          overflow: 'hidden',
        },
        videoWrapperSemitransparent: {
          backgroundColor: 'rgba(0,0,0,0.5)',
        },
        video: {
          width: '100%',
          height: '100%',
        },
        sideColumn: {
          flex: 1,
          alignSelf: 'stretch',
          gap: 10 * scale,
        },
        statusCard: {
          padding: 14 * scale,
          borderRadius: 10 * scale,
          backgroundColor: 'rgba(255,255,255,0.08)',
          gap: 4 * scale,
        },
        statusRow: {
          flexDirection: 'row',
          gap: 10 * scale,
        },
        statusLabel: {
          color: 'rgba(255,255,255,0.6)',
          fontSize: 12 * scale,
          width: 110 * scale,
        },
        statusValue: {
          color: '#fff',
          fontSize: 12 * scale,
          flex: 1,
        },
        button: {
          paddingVertical: 10 * scale,
          paddingHorizontal: 18 * scale,
          borderRadius: 10 * scale,
          borderWidth: 2,
          borderColor: 'transparent',
          alignSelf: 'flex-start',
          backgroundColor: 'rgba(255,255,255,0.15)',
        },
        buttonFocused: {
          backgroundColor: 'rgba(255,255,255,0.3)',
          borderColor: '#fff',
        },
        buttonText: {
          color: '#fff',
          fontSize: 14 * scale,
          fontWeight: '600',
        },
        logPanel: {
          flex: 1,
          padding: 12 * scale,
          borderRadius: 10 * scale,
          backgroundColor: 'rgba(0,0,0,0.35)',
        },
        logTitle: {
          color: 'rgba(255,255,255,0.8)',
          fontSize: 12 * scale,
          fontWeight: '700',
          marginBottom: 6 * scale,
        },
        logScroll: {
          flex: 1,
        },
        logContent: {
          gap: 2 * scale,
        },
        logLine: {
          color: 'rgba(255,255,255,0.9)',
          fontSize: 11 * scale,
          fontFamily: Platform.select({
            ios: 'Menlo',
            android: 'monospace',
            default: 'monospace',
          }),
        },
        logLineMuted: {
          color: 'rgba(255,255,255,0.4)',
          fontSize: 11 * scale,
          fontStyle: 'italic',
        },
      }),
    [scale],
  );
}
